import { useState } from 'react';
import { Plus } from 'lucide-react';
import type { CreateUserRequest, UpdateUserRequest, UserSummaryDto } from '@/api/types';
import { useCreateUser, useResetUserPassword, useUpdateUser, useUsers } from '@/api/queries';
import { UserDialog } from '@/components/users/UserDialog';
import { ResetPasswordDialog } from '@/components/users/ResetPasswordDialog';
import styles from '@/components/users/Users.module.css';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { useAuth } from '@/contexts/AuthContext';

export default function UsersPage() {
  const { user: currentUser, refreshUser } = useAuth();
  const usersQuery = useUsers();
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const resetMutation = useResetUserPassword();
  const [dialogUser, setDialogUser] = useState<UserSummaryDto | null | undefined>(undefined);
  const [resetUser, setResetUser] = useState<UserSummaryDto | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const busy = createMutation.isPending || updateMutation.isPending || resetMutation.isPending;

  async function createUser(request: CreateUserRequest) {
    const created = await createMutation.mutateAsync(request);
    setMessage(`Created ${created.displayName}.`);
    setDialogUser(undefined);
  }

  async function updateUser(id: string, request: UpdateUserRequest) {
    const updated = await updateMutation.mutateAsync({ id, request });
    setMessage(`Updated ${updated.displayName}.`);
    setDialogUser(undefined);
    if (id === currentUser?.id) await refreshUser();
  }

  async function resetPassword(temporaryPassword: string) {
    if (!resetUser) return;
    await resetMutation.mutateAsync({ user: resetUser, temporaryPassword });
    setMessage(`Reset the password for ${resetUser.displayName}.`);
    setResetUser(null);
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-lg">
        <h1>Users</h1>
        <Button onClick={() => setDialogUser(null)}><Plus size={16} /> Add user</Button>
      </div>

      {message && <Alert variant="success">{message}</Alert>}
      {usersQuery.error && <Alert variant="danger">{usersQuery.error.message}</Alert>}

      <div className="mt-lg" />
      <div className={styles.tableWrap}>
        <Table headers={['Name', 'Email', 'Role', 'Status', 'Actions']}>
          {usersQuery.data?.map((user) => (
            <tr key={user.id}>
              <td>{user.displayName}</td>
              <td>{user.email}</td>
              <td>{user.role}</td>
              <td><Badge variant={user.isActive ? 'success' : 'danger'}>{user.isActive ? 'Active' : 'Disabled'}</Badge></td>
              <td className={styles.actions}>
                <Button variant="ghost" onClick={() => setDialogUser(user)}>Edit</Button>
              </td>
            </tr>
          ))}
          {!usersQuery.isLoading && usersQuery.data?.length === 0 && (
            <tr><td className={styles.empty} colSpan={5}>No users found.</td></tr>
          )}
        </Table>
      </div>

      {dialogUser !== undefined && currentUser && (
        <UserDialog
          user={dialogUser}
          currentUserId={currentUser.id}
          busy={busy}
          onClose={() => setDialogUser(undefined)}
          onCreate={createUser}
          onUpdate={updateUser}
          onResetPassword={(target) => {
            setDialogUser(undefined);
            setResetUser(target);
          }}
        />
      )}
      {resetUser && (
        <ResetPasswordDialog
          user={resetUser}
          busy={busy}
          onClose={() => setResetUser(null)}
          onReset={resetPassword}
        />
      )}
    </div>
  );
}
